/**
 * Reading Spotify's own metadata to decide (a) whether its album date can be
 * trusted as the song's original year, and (b) what title to send to
 * MusicBrainz.
 *
 * Measured on a 500-track classic-metal playlist: Spotify's album date is the
 * *original* year even on remaster albums ("Paranoid (Remaster)" is dated
 * 1970-09-18), and where Spotify and MusicBrainz disagreed, neither was
 * reliably better. So a lookup is worth its second of rate-limited latency only
 * when Spotify's own metadata looks unreliable.
 */

/**
 * Decorations Spotify appends to a title. Used only to clean the string before
 * querying MusicBrainz — **not** to decide whether a lookup is needed.
 */
const VERSION_MARKERS = [
  'remaster',
  'remastered',
  'reissue',
  're-issue',
  'anniversary',
  'deluxe',
  'expanded',
  'special edition',
  'bonus track',
] as const

const MARKER_PATTERN = new RegExp(`\\b(${VERSION_MARKERS.join('|')})\\b`, 'i')

/**
 * A live recording's album date is the concert or live-album year, never the
 * year the song came out.
 *
 * Deliberately broad: this also matches "Long Live Rock 'n' Roll" and the song
 * "Live Wire", which are studio releases. That asymmetry is intentional — a
 * false positive costs one extra lookup (about two seconds), while a false
 * negative puts a wrong year on a printed card. On a 500-track playlist the
 * whole live+compilation set was 11 tracks, so over-triggering is free.
 */
const LIVE_PATTERN = /\b(live|unplugged|in concert)\b/i

/**
 * Compilation packaging: the album date is when the collection was assembled.
 *
 * `\b` is useless against Japanese, which has no word boundaries, so the CJK
 * terms are matched bare. Every entry below was measured on a real deck where
 * the album date was decades late: *All Singles TUBEst -Blue-* (2025) held a
 * 1992 single, and *森川美穂ベストコレクション* (2017) held a 1990 one.
 */
const COMPILATION_PATTERN =
  /\b(greatest hits|best of|the very best|anthology|the collection|essential|compilation|hits|all singles|self selection|selection|singles)\b|(?:ベスト|コレクション|全曲集|歌ってみた|主題歌集|大全集)/i

/**
 * A re-recording is a new performance, so the release date is its own — but the
 * song is older. `(2005 ver.)` and `[WANDS 第5期 ver.]` both dated a card
 * decades late.
 */
const RERECORDING_PATTERN =
  /[-–([<【]\s*(?:\d{4}|[^)\]>】]*?第\d+期)\s*(?:ver\.?|version)\s*[)\]>】]?|\bre-?recorded\b/i

/** A trailing year-ish decoration: `- 2012 - Remaster`, `(1998 Remastered Version)`. */
/**
 * Decorations that mark a different cut of the same song.
 *
 * `tv[- ]?(size|version)` and `edit` matter for anime in particular, where a
 * broadcast-length version sits beside the full one: MusicBrainz listed both
 * PENPALS "TELL ME WHY" and "TELL ME WHY (TV Version)", and the deck picked
 * both for 1997 — two cards playing the same song.
 */
const DECORATION_PATTERN =
  /\s*[-–([<]\s*(?:\d{4}\s*)?(?:digital\s+)?(?:remaster(?:ed)?|re-?issue|re-?recorded|remix|mono|stereo|single|album|radio|explicit|tv[-\s]?(?:size|version)|director['’]?s?[-\s]?(?:cut|edit)|edit|instrumental|karaoke|off\s?vocal)[^)\]>]*[)\]>]?\s*$/i

const TRAILING_DASH_SUFFIX = /\s+-\s+[^-]*$/

/**
 * Strips release decorations so the title matches what MusicBrainz stores.
 * Querying `"For Whom The Bell Tolls (Remastered)"` returns zero recordings;
 * `"For Whom the Bell Tolls"` returns a hundred.
 */
export function cleanTrackTitle(title: string): string {
  let cleaned = title.trim()
  // Repeat: Spotify stacks decorations, e.g. `Song - 2012 - Remaster`.
  for (let i = 0; i < 3; i++) {
    const next = cleaned.replace(DECORATION_PATTERN, '').trim()
    if (next === cleaned || next.length === 0) break
    cleaned = next
  }
  // `Song - 2011 Remaster` leaves `Song - 2011` on some catalogues.
  if (MARKER_PATTERN.test(title) && TRAILING_DASH_SUFFIX.test(cleaned)) {
    const trimmed = cleaned.replace(TRAILING_DASH_SUFFIX, '').trim()
    if (trimmed.length > 0) cleaned = trimmed
  }
  return cleaned.length > 0 ? cleaned : title.trim()
}

export interface SpotifyReleaseDescriptor {
  title: string
  albumName?: string | undefined
  /** Spotify `album_type`: `album`, `single` or `compilation`. */
  albumType?: string | undefined
}

export interface ReleaseClassification {
  /** True when Spotify's album date can stand in for the original release year. */
  looksOriginal: boolean
  reason: 'clean' | 'live' | 'compilation' | 're-recording' | 'unknown-title'
}

/**
 * Decides whether a MusicBrainz lookup is worth its second of rate-limited
 * latency.
 *
 * Only two cases justify it, and both are structural rather than cosmetic: the
 * album date describes a *package* instead of the song.
 *
 *  - **Live** records are dated by the concert.
 *  - **Compilations** are dated by when the collection was assembled.
 *
 * Remasters and deluxe editions deliberately do *not* trigger a lookup.
 * Measured over 22 remaster-marked tracks, Spotify's album date matched
 * MusicBrainz 18 times, and of the two disagreements Spotify was right in both
 * ("Seek & Destroy" 1983 not the 1982 demo; "Here I Go Again 87" 1987 not
 * 1992). Spotify keeps the original year on remaster albums — "Paranoid
 * (Remaster)" is dated 1970-09-18.
 */
export function classifySpotifyRelease(
  descriptor: SpotifyReleaseDescriptor,
): ReleaseClassification {
  if (descriptor.title.trim().length === 0) {
    return { looksOriginal: false, reason: 'unknown-title' }
  }
  const album = descriptor.albumName ?? ''
  const haystack = `${descriptor.title} ${album}`

  if ((descriptor.albumType ?? '').toLowerCase() === 'compilation') {
    return { looksOriginal: false, reason: 'compilation' }
  }
  if (LIVE_PATTERN.test(haystack)) {
    return { looksOriginal: false, reason: 'live' }
  }
  if (COMPILATION_PATTERN.test(album)) {
    return { looksOriginal: false, reason: 'compilation' }
  }
  // Checked against the title too: a re-recording is often issued as its own
  // single, so the album name carries no hint.
  if (RERECORDING_PATTERN.test(haystack)) {
    return { looksOriginal: false, reason: 're-recording' }
  }
  return { looksOriginal: true, reason: 'clean' }
}
